import _ from 'lodash'
import './resourceFilter.scss'
import templateUrl from './resourceFilter.html'

/**
 * Resource Filter component.
 *
 * Generates a widget for the {@link tmrResourceSearchForm Resource Search Form}.
 * Transcluded inside a {@link tmrResourceList Resource List} component, where the form lives.
 *
 * @example <caption>Filters by username with a text widget</caption>
 * <tmr-resource-filter
 *   label="ADMIN.USERS.RESOURCELIST.USERNAME"
 *   property="username"
 *   type="text"
 * ></tmr-resource-filter>
 *
 * @example <caption>Filters by multiple usernames with a chips widget</caption>
 * <tmr-resource-filter
 *   label="ADMIN.USERS.RESOURCELIST.USERNAME"
 *   property="username"
 *   type="text"
 *   multiple="true"
 * ></tmr-resource-filter>
 *
 * @example <caption>Filters by place label with an autocomplete widget</caption>
 * <tmr-resource-filter
 *   label="ADMIN.USERS.RESOURCELIST.PLACE"
 *   property="place.id"
 *   type="autocomplete"
 *   autocomplete-resource="Place"
 * ></tmr-resource-filter>
 *
 * @example <caption>Filters by place description with an autocomplete widget. Use with caution!</caption>
 * <tmr-resource-filter
 *   label="ADMIN.USERS.RESOURCELIST.PLACE"
 *   property="place.id"
 *   type="autocomplete"
 *   autocomplete-resource="Place"
 *   autocomplete-action="query"
 *   autocomplete-search-property="description"
 *   autocomplete-order-by="DESCRIPTION"
 * ></tmr-resource-filter>
 *
 * @example <caption>Filter by creation date with a date widget</caption>
 * <tmr-resource-filter
 *   label="RESOURCELIST.CREATIONDATEFILTER"
 *   property="creationDate"
 *   property-parts="['from', 'to']"
 *   type="date"
 *    max-date-disabled="true/false"
 * ></tmr-resource-filter>
 *
 * @example <caption>Filter by boolean select</caption>
 * <tmr-resource-filter
 *   label="REPORT.PARCELS.RESOURCELIST.COMPLETED"
 *   property="completed"
 *   type="bool"
 * ></tmr-resource-filter>
 *
 * @example <caption>Filters by username with a number widget</caption>
 * <tmr-resource-filter
 *   label="ADMIN.USERS.RESOURCELIST.NUMBERCODE"
 *   property="numberCode"
 *   property-parts="['from', 'to']"*
 *   type="number"
 *   range="true"
 *   rangeRequired="true"
 * ></tmr-resource-filter>*
 *
 * @type {ngComponent}
 * @prop {string} label
 * @prop {string} property
 * @prop {array<string>=} property-parts An array of sub parts of the property. Currently used by type 'date'.
 * @prop {'text'|'number'|'select'|'autocomplete'|'date'|'bool'} type The type of data, that determines the widget used
 * @prop {boolean} [multiple=false] Used by type 'text', 'select' and 'autocomplete'
 * @prop {boolean} [date-time=true] If true, show also time widget beside calendar
 * @prop {object} select-options
 * @prop {boolean} translate-options
 * @prop {string=} [autocomplete-action='search'] Defaults to "search"; `$resource` action called on autocomplete
 * @prop {string} autocomplete-resource The resource type
 * @prop {string} autocomplete-search-property Used only with custom autocomplete-action (not `search`)
 * @prop {string=} autocomplete-order-by Used only with custom autocomplete-action (not `search`)
 * @prop {string|function=} autocomplete-label The label of the resource shown in the list of search results. If string, it's a property on the resource; if function, receives the resource as argument and must return a string. If omitted, the label is the default for the resource type defined by `autocomplete-resource`.
 * @prop {boolean} [max-date-disabled] Used to disable md-max-date
 * @prop {boolean} [range=false] Used for 'number' to have a range "from -> to"
 * @prop {boolean} [rangeRequired=false] Used to require both 'from' and 'to' in range
 */
export const tmrResourceFilter = {
  ngType: 'component',
  templateUrl,
  controller: 'tmrResourceFilterController',
  bindings: {
    label: '@',
    property: '@',
    propertyParts: '<',
    type: '@',
    multiple: '<?',
    range: '<?',
    rangeRequired: '<?',
    // For type = date
    dateTime: '<',
    // For type = select
    selectOptions: '<',
    translateOptions: '<',
    // For type = autocomplete
    autocompleteAction: '@?',
    autocompleteResource: '@', // Required
    autocompleteSearchProperty: '@?',
    autocompleteLabel: '<?',
    autocompleteOrderBy: '@?',
    maxDateDisabled: '<', // enable/disable max date
  },
}

/**
 * Controller of component {@link tmrResourceFilter}.
 *
 * @type {ngController}
 */
export class tmrResourceFilterController {
  static ngType = 'controller'

  constructor(
    $filter,
    $injector,
    $mdConstant,
    $stateParams,
    $translate,
    TmrSearchFormFilters
  ) {
    'ngInject'
    Object.assign(this, {
      $filter,
      $injector,
      $mdConstant,
      $stateParams,
      $translate,
      TmrSearchFormFilters,
    })
  }

  $onInit() {
    // If propertyParts is set, value becomes an object and each part is
    // registered as a different filter with the format property.part
    if (Array.isArray(this.propertyParts)) {
      this.value = {}
      this.propertyParts.forEach(
        part =>
          (this.value[part] = this.$stateParams[this.property + '.' + part])
      )
    }
    // Otherwise, value is a string (or an array if multiple) and the simple property is registered.
    else this.value = this.$stateParams[this.property]

    // Maximum number of values for multiple fields with chips.
    // This is quite high by default; customizations can lower it if needed.
    this.maxChips = 150

    // Type-specific initializaton.
    if (this.type == 'text') this.initText()
    else if (this.type == 'autocomplete') this.initAutocomplete()
    else if (this.type == 'bool') this.initBool()
    else if (this.type == 'date') this.initDate()
    else if (this.type == 'number') this.initNumber()
    else if (this.type == 'select') this.initSelect()

    // Add filters to the global registry.
    let formatter = this.getFormatter()
    if (Array.isArray(this.propertyParts)) {
      this.propertyParts.forEach(part =>
        this.TmrSearchFormFilters.register(
          this.property + '.' + part,
          this.labelParts[part],
          this.value[part],
          formatter
        )
      )
    } else
      this.TmrSearchFormFilters.register(
        this.property,
        this.label,
        this.value,
        formatter
      )

    // set date max-date disabled
    this.disableMaxDate = this.maxDateDisabled === true ? true : false
  }

  $onDestroy() {
    // Remove filters from the global registry.
    if (Array.isArray(this.propertyParts)) {
      this.propertyParts.forEach(part =>
        this.TmrSearchFormFilters.unregister(this.property + '.' + part)
      )
    } else this.TmrSearchFormFilters.unregister(this.property)
  }

  /** Initializes a text filter */
  initText() {
    if (this.multiple) {
      if (!this.value) this.value = []
      if (this.value && !Array.isArray(this.value)) this.value = [this.value]
      this.initChipSeparators()
    }
  }

  /** Initialize number filter */
  initNumber() {
    if (this.range) {
      this.labelParts = {
        from:
          this.$translate.instant(this.label) +
          ' ' +
          this.$translate.instant('RESOURCELIST.FROMDATE'),
        to:
          this.$translate.instant(this.label) +
          ' ' +
          this.$translate.instant('RESOURCELIST.TODATE'),
      }
    }
  }

  /** Initialize select filter */
  initSelect() {
    if (this.multiple && this.value && !Array.isArray(this.value)) {
      this.value = [this.value]
    }
  }

  /** Initializes the characters that trigger a new chip in multiple text filters */
  initChipSeparators() {
    // Used to determine which key codes should split a chip when typing.
    this.chipSeparatorKeys = [
      this.$mdConstant.KEY_CODE.ENTER,
      this.$mdConstant.KEY_CODE.COMMA,
    ]
    // Used to determine which characters should split a chip when text is pasted in the box.
    this.chipSeparatorChars = [',']
  }

  /** Initializes an autocomplete filter */
  initAutocomplete() {
    if (!this.autocompleteResource) {
      throw new Error(
        'Autocomplete filter requires attribute autocomplete-resource'
      )
    }

    this.autocompleteAction = this.autocompleteAction ?? 'search'
    this.autocompleteProperty = this.property.split('.').pop()

    if (
      this.autocompleteAction !== 'search' &&
      !this.autocompleteSearchProperty
    ) {
      throw new Error(
        'Autocomplete filter with query action requires attribute autocomplete-search-property'
      )
    }

    // Initialize resource service.
    this.autocompleteService = this.$injector.get(this.autocompleteResource)

    // Multiple values.
    if (this.multiple) {
      this.chips = []
      if (this.value) {
        if (!Array.isArray(this.value)) this.value = [this.value]
        this.chips = this.value.map(id => {
          let chip = this.autocompleteService.create({ id })
          chip.$promise = chip.$get()
          return chip
        })
      }
    }
    // Single value.
    else if (this.value) {
      this.autocompleteItem = this.autocompleteService.create({
        id: this.value,
      })
      this.autocompleteItem.$promise = this.autocompleteItem.$get()
    }
  }

  /** Initializes a boolean filter */
  initBool() {
    this.translateOptions = true
    this.selectBoolOptions = [
      // Options for boolean select
      { label: 'RESOURCEFILTER.ANY', value: 'all' },
      { label: 'RESOURCEFILTER.TRUE', value: true },
      { label: 'RESOURCEFILTER.FALSE', value: false },
    ]
  }

  /** Initializes a date filter */
  initDate() {
    this.dateTime = this.dateTime ?? true
    this.labelParts = {
      from:
        this.$translate.instant(this.label) +
        ' ' +
        this.$translate.instant('RESOURCELIST.FROMDATE'),
      to:
        this.$translate.instant(this.label) +
        ' ' +
        this.$translate.instant('RESOURCELIST.TODATE'),
    }
  }

  /**
   * Selects an appropriate formatter for this filter type.
   * Formatters return objects with a `value` key that holds the formatted string.
   */
  getFormatter() {
    if (this.type == 'autocomplete')
      return (value, idx) => this.autocompleteFormatter(value, idx)
    if (this.type == 'date')
      return (value, idx) => this.dateFormatter(value, idx)
    if (this.type == 'select')
      return (value, idx) => this.selectFormatter(value, idx)
    if (this.type == 'bool')
      return (value, idx) => this.boolFormatter(value, idx)
  }

  autocompleteFormatter(value, idx) {
    let wrap = {}
    if (this.multiple)
      this.chips[idx].$promise.then(
        () => (wrap.value = this.autocompleteItemText(this.chips[idx]))
      )
    else
      this.autocompleteItem.$promise.then(
        () => (wrap.value = this.autocompleteItemText(this.autocompleteItem))
      )
    return wrap
  }

  dateFormatter(value) {
    return { value: this.$filter('tmrDate')(value) }
  }

  selectFormatter(value) {
    let wrap = {}
    if (this.translateOptions)
      this.$translate(this.selectOptions[value]).then(
        translated => (wrap.value = translated)
      )
    else wrap.value = this.selectOptions[value]
    return wrap
  }

  boolFormatter(value) {
    let wrap = { value }
    let option = this.selectBoolOptions.find(
      option =>
        (value === 'true' && option.value === true) ||
        (value === 'false' && option.value === false) ||
        (value === 'null' && option.value === null)
    )
    if (option)
      this.$translate(option.label).then(
        translated => (wrap.value = translated)
      )
    return wrap
  }

  textMultipleTransformChip(chip) {
    // Transform a string like "a,b, c, a " in an array like ['a', 'b', 'c']
    let regex = new RegExp(
      this.chipSeparatorChars.map(char => `\\${char}`).join('|')
    )
    let chips = _.uniq(chip.split(regex).map(chip => chip.trim())).filter(
      chip => chip && this.value.indexOf(chip) == -1
    )
    // Add chips but don't overflow max chips.
    if (this.value.length + chips.length > this.maxChips)
      chips = chips.slice(0, this.maxChips - this.value.length)
    this.value.push(...chips)
    // Pass the last chip to MdChipsCtrl so model validation and updates are performed.
    return chips.length ? this.value.pop() : null
  }

  /** React on filter change. */
  change() {
    // Update value on parent controller.
    if (Array.isArray(this.propertyParts)) {
      this.propertyParts.forEach(part =>
        this.TmrSearchFormFilters.update(
          this.property + '.' + part,
          this.value[part]
        )
      )
    } else this.TmrSearchFormFilters.update(this.property, this.value)
  }

  /** React on filter number change. */
  numberChange(newValue, oldValue, target) {
    // validation number text
    if (this.range) {
      this.value[target] = !/^-?\d*[.,]?\d*$/.test(newValue)
        ? oldValue
        : newValue
    } else {
      this.value = !/^-?\d*[.,]?\d*$/.test(newValue) ? oldValue : newValue
    }

    this.change()
  }

  /** Function to check from-to value and check for requiredRange prop */
  checkNumberRange(origin) {
    // check if fields has valid value
    if (
      this.value.from &&
      this.value.to &&
      parseInt(this.value.from) > parseInt(this.value.to)
    ) {
      if (origin === 'from') {
        this.value.to = this.value.from
      } else if (origin === 'to') {
        this.value.from = this.value.to
      }
    } else if (!!this.rangeRequired && (!this.value.from || !this.value.to)) {
      if (!this.value.from) {
        this.value.from = this.value.to
      } else {
        this.value.to = this.value.from
      }
    }
  }

  /** Perform autocomplete search. */
  autocomplete(searchText) {
    let params = this.autocompleteOrderBy
      ? { orderBy: this.autocompleteOrderBy, asc: 'true' }
      : {}
    params[
      this.autocompleteAction == 'search'
        ? 'value'
        : this.autocompleteSearchProperty
    ] = searchText
    return this.autocompleteService[this.autocompleteAction](params).$promise
  }

  /** Returns the display text of an autocomplete item. */
  autocompleteItemText(item) {
    if (typeof this.autocompleteLabel === 'string')
      return item[this.autocompleteLabel]
    else if (typeof this.autocompleteLabel === 'function')
      return this.autocompleteLabel(item)
    else if (this.autocompleteSearchProperty !== undefined)
      return item[this.autocompleteSearchProperty]
    else return item.$getLabel()
  }

  /** React on autocomplete change. */
  autocompleteChange() {
    this.value = this.autocompleteItem
      ? this.autocompleteItem[this.autocompleteProperty]
      : ''
    this.change()
  }

  /** React on chip (autocomplete multiple values) change. */
  chipChange() {
    this.value = this.chips.map(({ id }) => id)
    this.change()
  }

  /**
   * Determines when the autocomplete field should be rendered
   * Workaround for md-autocomplete trying to render the item before it's loaded
   */
  showAutocomplete() {
    // Meaning that it should render when there is no item, when there is
    // no promise (the item was already there), or the promise has resolved
    // (the item has been loaded)
    return (
      !this.autocompleteItem ||
      !this.autocompleteItem.$promise ||
      this.autocompleteItem.$resolved
    )
  }
}
